import { useTranslation } from 'react-i18next';
import {
    Button,
    Checkbox,
    Container,
    Divider,
    Dropdown,
    Icon,
    IconButton,
    Paper,
    Stack,
    styled,
    Text,
    TextInput,
} from 'shell/components';
import { getPrettyRole, getPrettySections, getUserPreferredFullName } from 'utils/helper';

const Mobile = ({
    accountUsers,
    sortState,
    filters,
    setFilters,
    getFilterCount,
    checked,
    setChecked,
    downloadCourseRoster,
    setSyncRosterDialog,
    getOptions,
    handleSelect,
}) => {
    const { t } = useTranslation();

    return (
        <Container device={'mobile'}>
            <RootStack sx={{ width: '100%' }} spacing={5}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                        {t('people.title', 'People')}
                    </Text>
                    <Button startIcon={<Icon kind={'download'} />} variant={'tertiary'} onClick={downloadCourseRoster}>
                        {t('people.course-roster', 'Course Roster')}
                    </Button>
                </Stack>
                <Stack direction={'row'} spacing={4}>
                    <Button
                        color={'neutral'}
                        variant={'secondary'}
                        startIcon={<Icon kind={'filter'} />}
                        onClick={() => setFilters((current) => ({ ...current, open: true }))}
                    >
                        ({getFilterCount(filters, sortState)})
                    </Button>
                    <TextInput
                        endIcon={<Icon kind={'search'} />}
                        placeholder={t('controls.search', 'Search')}
                        value={filters.search}
                        onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
                    />
                    <Button startIcon={<Icon kind={'resync'} />} onClick={() => setSyncRosterDialog({ open: true })}>
                        {t('controls.sync', 'Sync')}
                    </Button>
                </Stack>
                <Stack>
                    <CustomCheckbox
                        sx={{ width: 'fit-content' }}
                        aria-label={'select all'}
                        checked={
                            Boolean(accountUsers.length) &&
                            accountUsers.every(({ owner_account_id }) => checked.includes(owner_account_id))
                        }
                        label={t('controls.select-all', 'Select All')}
                        onChange={(e) => {
                            setChecked(
                                e.target.checked ? accountUsers.map(({ owner_account_id }) => owner_account_id) : []
                            );
                        }}
                    />
                    <Stack sx={{ pb: 10 }} spacing={3}>
                        <Divider />
                        {accountUsers.map((accountUser) => {
                            const accessibleName =
                                `${getUserPreferredFullName(accountUser)} (${accountUser.owner_account_id})`.trim();
                            const lastLoginDate = accountUser.login_activity
                                ? t('common:ui.format-date-short', {
                                      val: accountUser.login_activity.created_on,
                                  })
                                : '--';
                            return (
                                <Paper key={accountUser.owner_account_id} variant={'outlined'}>
                                    <Stack spacing={2}>
                                        <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                            <CustomCheckbox
                                                aria-label={`select ${accessibleName}`}
                                                checked={checked.includes(accountUser.owner_account_id)}
                                                onChange={(e) => {
                                                    setChecked((current) => {
                                                        return e.target.checked
                                                            ? [...current, accountUser.owner_account_id]
                                                            : current.filter(
                                                                  (item) => item !== accountUser.owner_account_id
                                                              );
                                                    });
                                                }}
                                            />
                                            <Stack sx={{ flex: 1 }} spacing={1}>
                                                <Text variant={'p1'} weight={'medium'}>
                                                    {getUserPreferredFullName(accountUser)}
                                                </Text>
                                                <Text color={'light'}>{accountUser.email}</Text>
                                            </Stack>
                                            <Dropdown
                                                id={`dropdown-${accountUser.owner_account_id}`}
                                                options={getOptions(accountUser)}
                                                rootComponent={
                                                    <IconButton
                                                        aria-label={`more options for ${accessibleName}`}
                                                        kind={'moreHorizontal'}
                                                        size={'small'}
                                                        variant={'square'}
                                                    />
                                                }
                                                onSelect={({ key }) => handleSelect({ key }, accountUser)}
                                            />
                                        </Stack>
                                        <Divider />
                                        <Stack spacing={3}>
                                            <Text color={'light'}>
                                                {t('table.role', 'Role')}: {getPrettyRole(accountUser.role, t)}
                                            </Text>
                                            <Text color={'light'}>
                                                {t('table.section-s', 'Section(s)')}:{' '}
                                                {getPrettySections(accountUser.sections)}
                                            </Text>
                                            <Text color={'light'}>
                                                {t('table.last-activity', 'Last activity')}: {lastLoginDate}
                                            </Text>
                                        </Stack>
                                    </Stack>
                                </Paper>
                            );
                        })}
                    </Stack>
                </Stack>
            </RootStack>
        </Container>
    );
};

const RootStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const CustomCheckbox = styled(Checkbox)(() => {
    return {
        paddingLeft: 0,
        '& .MuiCheckbox-root': {
            paddingLeft: 0,
        },
    };
});

export default Mobile;
